<template>
  <div class="card-resumen">
    <div class="p-20">
      <div class="p text-landing semibold pb-3">
        {{ $t('reserva.eligeMetodoPago') }}
      </div>
      <div class="row g-2">
        <div v-for="pasarela in pasarelasList" :key="pasarela.id" class="col-6">
          <input
            :id="'pasarela-' + pasarela.id"
            v-model="selectedPasarelaId"
            :disabled="isPasarelaDisabled(pasarela)"
            type="radio"
            name="metodoPago"
            :value="pasarela.id"
            class="d-none"
            @change="onSelect(pasarela)" />

          <label
            :for="'pasarela-' + pasarela.id"
            class="metodo-item w-100 d-flex flex-column flex-md-row align-items-center justify-content-center pointer p-3 rounded position-relative"
            :class="{
              'metodo-item-selected': selectedPasarelaId === pasarela.id,
              'metodo-disabled': isPasarelaDisabled(pasarela),
            }">
            <span class="metodo-item-icon position-absolute top-0 end-0">
              <RadioButtonIcon :is-selected="selectedPasarelaId === pasarela.id" />
            </span>
            <CustomIcon
              v-if="pasarela.icon"
              :icon="pasarela.icon"
              :alt="pasarela.integracion"
              class="metodo-item-icon-svg mb-2 mb-md-0 me-md-2 text-landing" />
            <span class="metodo-item-text small text-landing d-md-none text-center">{{ pasarela.tituloMobile }}</span>
            <span class="metodo-item-text p text-landing d-none d-md-inline text-center">{{ pasarela.titulo }}</span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, onBeforeMount, ref } from 'vue';
  import RadioButtonIcon from '@/components/Icons/RadioButtonIcon.vue';
  import { normalizePasarelasPago } from '@/helpers/pagosUtils';
  import CustomIcon from '@/components/CustomIcon.vue';

  const props = defineProps({
    pasarelasPago: {
      type: Object,
      required: true,
    },
    alojamiento: {
      type: Object,
      default: () => null,
    },
    initialPasarelaPago: {
      type: Object,
      default: () => null,
    },
  });

  const emit = defineEmits(['metodo-pago-seleccionado']);

  const selectedPasarelaId = ref(null);

  const pasarelasList = computed(() => normalizePasarelasPago(props.pasarelasPago));

  onBeforeMount(() => {
    const pasarelaInicial = resolvePasarelaInicial();
    if (!pasarelaInicial) return;

    selectedPasarelaId.value = pasarelaInicial.id;
    emit('metodo-pago-seleccionado', pasarelaInicial);
  });

  function resolvePasarelaInicial() {
    const list = pasarelasList.value;
    if (!list?.length) return null;

    const initialId = props.initialPasarelaPago?.id;
    if (initialId != null) {
      const match = list.find(pasarela => Number(pasarela.id) === Number(initialId));
      if (match && !isPasarelaDisabled(match)) return match;
    }

    return list.find(pasarela => !isPasarelaDisabled(pasarela)) ?? list[0];
  }

  function onSelect(pasarela) {
    selectedPasarelaId.value = pasarela.id;
    emit('metodo-pago-seleccionado', pasarela);
  }

  function isPasarelaDisabled(pasarela) {
    return (
      props.alojamiento !== null &&
      props.alojamiento.habitacion.integrado &&
      ((!props.alojamiento.habitacion.doesBlockBookingBeforePayment && !pasarela.refundEnabled) || pasarela.manual)
    );
  }
</script>

<style scoped lang="scss">
  .card-resumen {
    border-radius: var(--bs-border-radius-xl);
    border: 1px solid rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.25);
  }

  .metodo-item {
    min-height: 56px;
    border: 1px solid rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.25);
    transition: background-color 0.2s ease, border-color 0.2s ease;

    @media (max-width: 768px) {
      min-height: 72px;
    }
  }

  .metodo-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .metodo-item:hover:not(.metodo-disabled) {
    background-color: rgba(var(--landing-detail-color-rgb, var(--bs-primary-rgb)), 0.1);
  }

  .metodo-item-selected {
    background-color: rgba(var(--landing-detail-color-rgb, var(--bs-secondary-rgb)), 0.1);
    border-color: var(--landing-detail-color, var(--bs-secondary));
  }

  .metodo-item-icon {
    margin: 6px;
    transform: scale(0.8);
    transform-origin: top right;
  }

  .metodo-item-icon-svg {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }

  .metodo-item-text {
    text-align: center;
  }
</style>
